"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback } from "react";
import {
  getCurrentSchoolSession,
  issueSession,
  clearSchoolSession,
  storeAuthToken,
} from "./auth";
import {
  extractSchoolSlug,
  buildSchoolUrl,
  checkIfInSchoolContext,
} from "./url-utils";
import { SchoolRole, School } from "@/types/school";
import { call } from "@/lib/functionsApi";
import { PLATFORM_NAME } from "./constants";
import { LOCALS } from "@/consts/settings";

export type NavigationMethod =
  | "router.push"
  | "router.replace"
  | "window.location.href";

export interface NavigationOptions {
  locale?: string;
  method?: NavigationMethod;
  reason?: string;
}

// Re-export utilities for convenience (but they come from url-utils.ts)
export {
  extractSchoolSlug,
  extractSlug,
  buildSchoolUrl,
  checkIfInSchoolContext,
} from "./url-utils";

/**
 * Centralized navigation service that handles all routing and redirection logic
 */
export function useNavigation() {
  const router = useRouter();
  const pathname = usePathname();

  const log = (action: string, data: Record<string, unknown>) => {
    console.log(`[Navigation] ${action}:`, data);
  };

  /**
   * Navigate to any path with proper school context handling
   */
  const navigateTo = useCallback(
    (path: string, options: NavigationOptions = {}) => {
      const currentLocale =
        LOCALS.find((l) => pathname.startsWith(`/${l.code}`))?.code || "en";

      const { locale = currentLocale, method, reason } = options;
      const session = getCurrentSchoolSession();

      log("Navigation requested", {
        path,
        locale,
        requestedMethod: method,
        hasSession: !!session,
        sessionSchool: session?.schoolSlug,
        reason,
      });

      // Determine the best navigation method
      let finalMethod: NavigationMethod;
      let finalUrl: string;

      if (session && path !== "/" && !path.startsWith("http")) {
        // School-scoped navigation - use full URL with subdomain
        finalUrl = buildSchoolUrl(session.schoolSlug, locale, path);
        finalMethod = method || "router.push";

        log("School-scoped navigation", {
          schoolSlug: session.schoolSlug,
          path,
          finalUrl,
          method: finalMethod,
        });
      } else {
        // Regular navigation or home page
        finalUrl = path.startsWith("http")
          ? path
          : `/${locale}${path.startsWith("/") ? "" : "/"}${path}`;
        finalMethod = method || "router.push";

        log("Regular navigation", {
          path,
          finalUrl,
          method: finalMethod,
          reason: session ? "Home page navigation" : "No school session",
        });
      }

      // Execute navigation
      switch (finalMethod) {
        case "router.push":
          router.push(finalUrl);
          break;
        case "router.replace":
          router.replace(finalUrl);
          break;
        case "window.location.href":
          window.location.href = finalUrl;
          break;
      }
    },
    [router, pathname]
  );

  /**
   * Navigate to home page, clearing any school context
   */
  const navigateToHome = useCallback(
    (options: NavigationOptions = {}) => {
      log("Navigating to home", { clearSession: true });
      // Always use router for home navigation to avoid subdomain issues
      navigateTo("/", { ...options, method: "router.push" });
    },
    [navigateTo]
  );

  /**
   * Navigate to a school subdomain with proper authentication setup
   */
  const navigateToSchoolSubdomain = useCallback(
    async (schoolUrl: string, schoolSlug: string, reason: string) => {
      console.log("🚀 [Navigation] Starting school subdomain navigation", {
        schoolUrl,
        schoolSlug,
        reason,
        timestamp: new Date().toISOString(),
        currentUrl:
          typeof window !== "undefined" ? window.location.href : "N/A",
      });

      // Give cookies time to be set and propagate
      console.log("⏱️ [Navigation] Waiting for cookie propagation (200ms)");
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Verify authentication cookies are set before navigation
      if (typeof window !== "undefined") {
        const allCookies = document.cookie;
        const authCookie = allCookies.includes("firebaseAuthToken=");
        const sessionCookie = allCookies.includes("schoolSession=");

        console.log("🍪 [Navigation] Pre-navigation cookie analysis:", {
          hasAuthCookie: authCookie,
          hasSessionCookie: sessionCookie,
          allCookiesCount: allCookies.split(";").length,
          cookieNames: allCookies.split(";").map((c) => c.trim().split("=")[0]),
          fullCookieString: allCookies,
        });

        // Additional wait if cookies aren't detected yet (max 2 more attempts)
        let attempts = 0;
        while ((!authCookie || !sessionCookie) && attempts < 2) {
          console.log(
            `⏳ [Navigation] Cookie retry attempt ${attempts + 1}/2`,
            {
              hasAuthCookie: authCookie,
              hasSessionCookie: sessionCookie,
              waitingMs: 300,
            }
          );
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Re-check cookies
          const newAllCookies = document.cookie;
          const newAuthCookie = newAllCookies.includes("firebaseAuthToken=");
          const newSessionCookie = newAllCookies.includes("schoolSession=");

          console.log(
            `🔄 [Navigation] Cookie recheck after attempt ${attempts + 1}:`,
            {
              newAuthCookie,
              newSessionCookie,
              newCookieString: newAllCookies,
            }
          );

          if (newAuthCookie && newSessionCookie) {
            console.log(
              "✅ [Navigation] Cookies detected on retry, proceeding"
            );
            break;
          }

          attempts++;
        }

        // Final cookie status before navigation
        const finalCookies = document.cookie;
        console.log("🏁 [Navigation] Final cookie status before navigation:", {
          finalAuthCookie: finalCookies.includes("firebaseAuthToken="),
          finalSessionCookie: finalCookies.includes("schoolSession="),
          finalCookieString: finalCookies.slice(0, 50), // Truncate for brevity
          aboutToNavigateTo: schoolUrl,
        });
      }

      // Navigate to the school subdomain
      console.log("🌐 [Navigation] Executing navigation to subdomain", {
        fromUrl: typeof window !== "undefined" ? window.location.href : "N/A",
        toUrl: schoolUrl,
        method: "window.location.href",
      });

      if (typeof window !== "undefined") {
        window.location.href = schoolUrl;
      }
    },
    []
  );

  /**
   * Select a school and navigate to its dashboard
   */
  const selectSchool = useCallback(
    async (
      school: School,
      userId: string,
      roles: SchoolRole[] = ["student"],
      options: NavigationOptions = {}
    ) => {
      console.log("🎯 [Navigation] School selection initiated", {
        schoolId: school.id,
        schoolName: school.name,
        schoolSlug: school.slug,
        userId: userId.substring(0, 8) + "...",
        roles,
        options,
        timestamp: new Date().toISOString(),
      });

      try {
        // Store Firebase auth token for cross-subdomain navigation
        console.log("🔐 [Navigation] Storing Firebase auth token...");
        const authTokenResult = await storeAuthToken();
        console.log("🔐 [Navigation] Auth token storage result:", {
          success: !!authTokenResult,
          tokenPreview: authTokenResult
            ? authTokenResult.substring(0, 20) + "..."
            : null,
        });

        // Issue session for the selected school
        console.log("🎫 [Navigation] Issuing school session...", {
          schoolSlug: school.slug,
          roles,
          userId: userId.substring(0, 8) + "...",
        });
        const sessionResult = issueSession(
          userId,
          school.id,
          school.slug,
          roles
        );
        console.log("🎫 [Navigation] School session issued:", {
          sessionCreated: !!sessionResult,
          sessionPreview: sessionResult
            ? sessionResult.substring(0, 50) + "..."
            : null,
        });

        // Build the school URL
        const schoolUrl = buildSchoolUrl(
          school.slug,
          options.locale || "en",
          "/courses",
          true // Force full URL to ensure subdomain navigation
        );
        console.log("🔗 [Navigation] Built school URL:", {
          schoolUrl,
          slug: school.slug,
          locale: options.locale || "en",
          path: "/courses",
        });

        // Generate a Firebase Custom Token while we're still authenticated on the parent domain
        try {
          console.log("🔐 [Navigation] Requesting custom token for handoff...");
          const result = await call("getCustomToken", {});
          const customToken = (result as { customToken?: string })?.customToken;
          if (customToken && typeof window !== "undefined") {
            // Determine base cookie domain for cross-subdomain access
            const host = window.location.host;
            let domain: string;
            if (host.includes(`${PLATFORM_NAME}.com`)) {
              domain = `.${PLATFORM_NAME}.com`;
            } else {
              const hostParts = host.split(".");
              domain =
                hostParts.length >= 2
                  ? `.${hostParts.slice(-2).join(".")}`
                  : host;
            }

            // Set short-lived handoff cookie (~60s)
            document.cookie = `firebaseCustomToken=${customToken}; path=/; domain=${domain}; max-age=60; SameSite=Lax`;
            console.log("🔐 [Navigation] Custom token handoff cookie set:", {
              domain,
              tokenPreview: customToken.substring(0, 12) + "...",
            });
          } else {
            console.warn(
              "⚠️ [Navigation] No custom token returned for handoff"
            );
          }
        } catch (err) {
          console.error(
            "❌ [Navigation] Failed to create custom token for handoff:",
            err
          );
        }

        // Navigate with proper authentication setup
        await navigateToSchoolSubdomain(
          schoolUrl,
          school.slug,
          "School selection"
        );
      } catch (error) {
        console.error("❌ [Navigation] School selection failed:", error);
        console.log("❌ [Navigation] School selection error details:", {
          error: error instanceof Error ? error.message : "Unknown error",
          schoolSlug: school.slug,
          userId: userId.substring(0, 8) + "...",
          stack: error instanceof Error ? error.stack : null,
        });
      }
    },
    [navigateToSchoolSubdomain]
  );

  /**
   * Create a new school and navigate to it
   */
  const createAndSelectSchool = useCallback(
    async (school: School, userId: string, options: NavigationOptions = {}) => {
      console.log("🏫 [Navigation] School creation and selection initiated", {
        schoolId: school.id,
        schoolName: school.name,
        schoolSlug: school.slug,
        userId: userId.substring(0, 8) + "...",
        options,
        timestamp: new Date().toISOString(),
      });

      try {
        // Store Firebase auth token for cross-subdomain navigation
        console.log(
          "🔐 [Navigation] Storing Firebase auth token for new school..."
        );
        const authTokenResult = await storeAuthToken();
        console.log("🔐 [Navigation] Auth token storage result:", {
          success: !!authTokenResult,
          tokenPreview: authTokenResult
            ? authTokenResult.substring(0, 20) + "..."
            : null,
        });

        // Issue session as owner for the new school
        console.log("👑 [Navigation] Issuing owner session for new school...", {
          schoolSlug: school.slug,
          roles: ["owner"],
          userId: userId.substring(0, 8) + "...",
        });
        const sessionResult = issueSession(userId, school.id, school.slug, [
          "owner",
        ]);
        console.log("👑 [Navigation] Owner session issued:", {
          sessionCreated: !!sessionResult,
          sessionPreview: sessionResult
            ? sessionResult.substring(0, 50) + "..."
            : null,
        });

        // Build the school URL
        const schoolUrl = buildSchoolUrl(
          school.slug,
          options.locale || "en",
          "/courses",
          true // Force full URL to ensure subdomain navigation
        );
        console.log("🔗 [Navigation] Built new school URL:", {
          schoolUrl,
          slug: school.slug,
          locale: options.locale || "en",
          path: "/courses",
        });

        // Generate a Firebase Custom Token while we're still authenticated on the parent domain
        try {
          console.log(
            "🔐 [Navigation] Requesting custom token for handoff (new school)..."
          );
          const result = await call("getCustomToken", {});
          const customToken = (result as { customToken?: string })?.customToken;
          if (customToken && typeof window !== "undefined") {
            const host = window.location.host;
            let domain: string;
            if (host.includes(`${PLATFORM_NAME}.com`)) {
              domain = `.${PLATFORM_NAME}.com`;
            } else {
              const hostParts = host.split(".");
              domain =
                hostParts.length >= 2
                  ? `.${hostParts.slice(-2).join(".")}`
                  : host;
            }
            document.cookie = `firebaseCustomToken=${customToken}; path=/; domain=${domain}; max-age=60; SameSite=Lax`;
            console.log(
              "🔐 [Navigation] Custom token handoff cookie set (new school):",
              {
                domain,
                tokenPreview: customToken.substring(0, 12) + "...",
              }
            );
          } else {
            console.warn(
              "⚠️ [Navigation] No custom token returned for handoff (new school)"
            );
          }
        } catch (err) {
          console.error(
            "❌ [Navigation] Failed to create custom token for handoff (new school):",
            err
          );
        }

        // Navigate with proper authentication setup
        await navigateToSchoolSubdomain(
          schoolUrl,
          school.slug,
          "New school creation"
        );
      } catch (error) {
        console.error(
          "❌ [Navigation] School creation and selection failed:",
          error
        );
        console.log("❌ [Navigation] School creation error details:", {
          error: error instanceof Error ? error.message : "Unknown error",
          schoolSlug: school.slug,
          userId: userId.substring(0, 8) + "...",
          stack: error instanceof Error ? error.stack : null,
        });
      }
    },
    [navigateToSchoolSubdomain]
  );

  /**
   * Handle user logout with proper cleanup
   */
  const logout = useCallback(
    async (
      logoutFunction: () => Promise<void>,
      options: NavigationOptions = {}
    ) => {
      log("User logout initiated", {});

      // Clear school session
      clearSchoolSession();

      // Call auth logout
      await logoutFunction();

      // Navigate to home
      navigateToHome({ ...options, reason: "User logout" });
    },
    [navigateToHome]
  );

  /**
   * Switch language while preserving current path and school context
   */
  const switchLanguage = useCallback(
    (
      newLocale: string,
      currentPathname: string,
      supportedLocales: string[],
      options: NavigationOptions = {}
    ) => {
      log("Language switch requested", {
        currentLocale:
          supportedLocales.find((l) => currentPathname.startsWith(`/${l}`)) ||
          "en",
        newLocale,
        currentPathname,
      });

      let newPath: string;
      const currentLocale = supportedLocales.find((l) =>
        currentPathname.startsWith(`/${l}`)
      );

      if (currentLocale) {
        // Replace existing locale
        newPath = currentPathname.replace(`/${currentLocale}`, `/${newLocale}`);
      } else {
        // Add locale prefix
        newPath = `/${newLocale}${
          currentPathname.startsWith("/") ? "" : "/"
        }${currentPathname}`;
      }

      log("Language switch navigation", {
        currentLocale,
        newLocale,
        originalPath: currentPathname,
        newPath,
      });

      log(pathname, { pathname });

      // remove local from new path
      if (newPath.startsWith(`/${newLocale}/`)) {
        newPath = newPath.substring(newLocale.length + 1);
      } else if (newPath === `/${newLocale}`) {
        newPath = "/";
      }

      log("Final path after locale adjustment", { newPath });

      // Use router.replace for language switches to avoid history pollution
      navigateTo(newPath, {
        ...options,
        method: "router.replace",
        reason: "Language switch",
      });
    },
    [navigateTo, pathname]
  );

  /**
   * Handle school context redirection (used in useEffect hooks)
   */
  const handleSchoolContextRedirect = useCallback(
    (
      user: { uid: string } | null,
      currentPathname: string,
      options: NavigationOptions = {}
    ) => {
      if (typeof window === "undefined") return false;

      const host = window.location.host;
      const schoolSlug = extractSchoolSlug(host);

      log("School context check", {
        host,
        schoolSlug,
        hasUser: !!user,
        currentPath: currentPathname,
      });

      // If we're in a school context and user is logged in
      if (schoolSlug && user) {
        const session = getCurrentSchoolSession();

        // If they have a valid session for this school and are on the home page,
        // redirect them to the school dashboard
        if (
          session &&
          session.schoolSlug === schoolSlug &&
          currentPathname === "/en"
        ) {
          log("Redirecting to school dashboard", {
            schoolSlug,
            userId: user.uid,
            reason: "User has valid session and is on home page",
          });

          navigateTo("/courses", {
            ...options,
            method: "router.replace",
            reason: "School context redirect to dashboard",
          });
          return true;
        }

        // If they don't have a session for this school, redirect to main domain
        if (!session || session.schoolSlug !== schoolSlug) {
          const redirectPath = `/${currentPathname.split("/")[1] || "en"}`;
          log("Redirecting from school context (no valid session)", {
            schoolSlug,
            sessionSchool: session?.schoolSlug || null,
            userId: user.uid,
            redirectPath,
            reason: "No valid session for this school",
          });

          // Use window.location to navigate to main domain
          window.location.href = `${
            window.location.protocol
          }//${window.location.host
            .split(".")
            .slice(-2)
            .join(".")}${redirectPath}`;
          return true;
        }
      }

      return false;
    },
    [navigateTo]
  );

  /**
   * Handle authentication redirect for protected routes
   */
  const handleAuthRedirect = useCallback(
    (
      user: { uid: string } | null,
      currentPathname: string,
      isPublicRoute: (path: string) => boolean,
      options: NavigationOptions = {}
    ) => {
      if (typeof window === "undefined") return false;

      const inSchoolContext = checkIfInSchoolContext(window.location.host);

      log("Auth check", {
        hasUser: !!user,
        userId: user?.uid,
        pathname: currentPathname,
        isPublicRoute: isPublicRoute(currentPathname),
        inSchoolContext,
        host: window.location.host,
      });

      if (!user && !isPublicRoute(currentPathname) && !inSchoolContext) {
        log("Redirecting unauthorized user", {
          pathname: currentPathname,
          reason: "User not authenticated and route is protected",
        });

        navigateToHome({
          ...options,
          method: "router.replace",
          reason: "Authentication required",
        });
        return true;
      }

      return false;
    },
    [navigateToHome]
  );

  return {
    navigateTo,
    navigateToHome,
    selectSchool,
    createAndSelectSchool,
    logout,
    switchLanguage,
    handleSchoolContextRedirect,
    handleAuthRedirect,
  };
}
